import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';

// Notification types
export const NotificationType = {
  SCAN_STARTED: 'scan_started',
  SCAN_PROGRESS: 'scan_progress',
  SCAN_COMPLETED: 'scan_completed',
  SCAN_FAILED: 'scan_failed',
  CRITICAL_ALERT: 'critical_alert',
  HIGH_ALERT: 'high_alert',
  SECURITY_ALERT: 'security_alert',
  CONNECTION_ACK: 'connection_ack',
  HEARTBEAT: 'heartbeat',
  ERROR: 'error',
};

// Notification channels
export const NotificationChannel = {
  AMIDALA: 'amidala',
  LITESABER: 'litesaber',
  ALERTS: 'alerts',
  GLOBAL: 'global',
};

// WebSocket context
const WebSocketContext = createContext(null);

// Get WebSocket URL based on current location
const getWebSocketUrl = () => {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const host = window.location.host;
  
  // In development, the WebSocket might be on the API server
  const apiUrl = process.env.REACT_APP_BACKEND_URL;
  if (apiUrl) {
    try {
      const url = new URL(apiUrl);
      return `${protocol}//${url.host}/ws`;
    } catch {
      return `${protocol}//${host}/ws`;
    }
  }
  
  return `${protocol}//${host}/ws`;
};

export const WebSocketProvider = ({ children }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [connectionId, setConnectionId] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [scanProgress, setScanProgress] = useState({}); // {scanId: {progress, status, ...}}
  const [alerts, setAlerts] = useState([]);
  
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const listenersRef = useRef(new Map());
  
  // Connect to WebSocket
  const connect = useCallback((channels = ['global', 'alerts']) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }
    
    const token = localStorage.getItem('token');
    const wsUrl = getWebSocketUrl();
    const channelsParam = channels.join(',');
    const url = token 
      ? `${wsUrl}?token=${token}&channels=${channelsParam}`
      : `${wsUrl}?channels=${channelsParam}`;
    
    console.log('WebSocket connecting to:', url);
    
    try {
      wsRef.current = new WebSocket(url);
      
      wsRef.current.onopen = () => {
        console.log('WebSocket connected');
        setIsConnected(true);
        
        // Clear any pending reconnect
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
          reconnectTimeoutRef.current = null;
        }
      };
      
      wsRef.current.onclose = (event) => {
        console.log('WebSocket disconnected:', event.code, event.reason);
        setIsConnected(false);
        setConnectionId(null);
        
        // Auto-reconnect after 5 seconds
        if (!reconnectTimeoutRef.current) {
          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectTimeoutRef.current = null;
            connect(channels);
          }, 5000);
        }
      };
      
      wsRef.current.onerror = (error) => {
        console.error('WebSocket error:', error);
      };
      
      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          handleMessage(data);
        } catch (e) {
          console.error('Failed to parse WebSocket message:', e);
        }
      };
    } catch (error) {
      console.error('Failed to create WebSocket:', error);
    }
  }, []);
  
  // Disconnect from WebSocket
  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    
    setIsConnected(false);
    setConnectionId(null);
  }, []);
  
  // Handle incoming messages
  const handleMessage = useCallback((data) => {
    const { type, ...payload } = data;
    
    // Generate unique ID using timestamp + random string
    const generateUniqueId = () => `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    // Add to notifications list
    if (type !== NotificationType.HEARTBEAT) {
      setNotifications(prev => [
        { id: generateUniqueId(), type, ...payload, receivedAt: new Date() },
        ...prev.slice(0, 99) // Keep last 100 notifications
      ]);
    }
    
    // Handle specific message types
    switch (type) {
      case NotificationType.CONNECTION_ACK:
        setConnectionId(payload.connection_id);
        break;
        
      case NotificationType.SCAN_STARTED:
        setScanProgress(prev => ({
          ...prev,
          [payload.scan_id]: {
            ...payload,
            progress: 0,
            status: 'running'
          }
        }));
        break;
        
      case NotificationType.SCAN_PROGRESS:
        setScanProgress(prev => ({
          ...prev,
          [payload.scan_id]: {
            ...prev[payload.scan_id],
            ...payload
          }
        }));
        break;
        
      case NotificationType.SCAN_COMPLETED:
        setScanProgress(prev => ({
          ...prev,
          [payload.scan_id]: {
            ...prev[payload.scan_id],
            ...payload,
            progress: 100,
            status: 'completed'
          }
        }));
        break;
        
      case NotificationType.SCAN_FAILED:
        setScanProgress(prev => ({
          ...prev,
          [payload.scan_id]: {
            ...prev[payload.scan_id],
            ...payload,
            status: 'failed'
          }
        }));
        break;
        
      case NotificationType.CRITICAL_ALERT:
      case NotificationType.HIGH_ALERT:
      case NotificationType.SECURITY_ALERT:
        setAlerts(prev => [
          { id: generateUniqueId(), type, ...payload, receivedAt: new Date() },
          ...prev.slice(0, 49) // Keep last 50 alerts
        ]);
        break;
        
      default:
        break;
    }
    
    // Call registered listeners
    const listeners = listenersRef.current.get(type) || [];
    listeners.forEach(callback => callback(payload));
    
    // Also call 'all' listeners
    const allListeners = listenersRef.current.get('all') || [];
    allListeners.forEach(callback => callback(data));
  }, []);
  
  // Send a message through WebSocket
  const send = useCallback((data) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(data));
    }
  }, []);
  
  // Subscribe to a channel
  const subscribe = useCallback((channels) => {
    send({ type: 'subscribe', channels: Array.isArray(channels) ? channels : [channels] });
  }, [send]);
  
  // Unsubscribe from a channel
  const unsubscribe = useCallback((channels) => {
    send({ type: 'unsubscribe', channels: Array.isArray(channels) ? channels : [channels] });
  }, [send]);
  
  // Add event listener for specific notification types
  const addListener = useCallback((type, callback) => {
    if (!listenersRef.current.has(type)) {
      listenersRef.current.set(type, []);
    }
    listenersRef.current.get(type).push(callback);
    
    // Return cleanup function
    return () => {
      const listeners = listenersRef.current.get(type) || [];
      const index = listeners.indexOf(callback);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    };
  }, []);
  
  // Remove event listener
  const removeListener = useCallback((type, callback) => {
    const listeners = listenersRef.current.get(type) || [];
    const index = listeners.indexOf(callback);
    if (index > -1) {
      listeners.splice(index, 1);
    }
  }, []);
  
  // Clear notifications
  const clearNotifications = useCallback(() => {
    setNotifications([]);
  }, []);
  
  // Clear alerts
  const clearAlerts = useCallback(() => {
    setAlerts([]);
  }, []);
  
  // Clear scan progress for a specific scan
  const clearScanProgress = useCallback((scanId) => {
    setScanProgress(prev => {
      const next = { ...prev };
      delete next[scanId];
      return next;
    });
  }, []);
  
  // Get progress for a specific scan
  const getScanProgress = useCallback((scanId) => {
    return scanProgress[scanId] || null;
  }, [scanProgress]);
  
  // Auto-connect when component mounts
  useEffect(() => {
    connect([NotificationChannel.GLOBAL, NotificationChannel.ALERTS]);
    
    return () => {
      disconnect();
    };
  }, []);
  
  const value = {
    isConnected,
    connectionId,
    notifications,
    scanProgress,
    alerts,
    connect,
    disconnect,
    send,
    subscribe,
    unsubscribe,
    addListener,
    removeListener,
    clearNotifications,
    clearAlerts,
    clearScanProgress,
    getScanProgress,
  };
  
  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};

// Custom hook to use WebSocket context
export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};

// Custom hook for scan progress
export const useScanProgress = (scanId) => {
  const { scanProgress, clearScanProgress } = useWebSocket();
  
  const progress = scanProgress[scanId] || null;
  
  const clear = useCallback(() => {
    clearScanProgress(scanId);
  }, [scanId, clearScanProgress]);
  
  return { progress, clear };
};

// Custom hook for listening to specific notification types
export const useNotificationListener = (type, callback) => {
  const { addListener } = useWebSocket();
  
  useEffect(() => {
    const cleanup = addListener(type, callback);
    return cleanup;
  }, [type, callback, addListener]);
};

export default WebSocketContext;
